import { ref, uploadBytes } from 'firebase/storage';
import {
  ApiResumeUploadVideo,
  fetcher,
  isApiV1Errors,
  isResumeUploadVideoOk,
} from '@utils/data-fetching';
import { bjToast } from '@components/common';
import storage, { STORAGE_BUCKETS_URI } from '@services/firebase/firebaseConfig';

/**
 * Uploads a video file to Firebase storage and saves the video information via API
 * @param file - The file to upload
 * @param locale - The locale for API calls
 * @param interviewQuestionId - The question associated with the video
 * @param bucketFolder - The folder in the storage bucket to upload to
 * @param onComplete - Callback function called when upload is complete
 * @returns Promise that resolves when the upload is complete
 */
export const uploadJobInterviewVideo = async (
  file: File,
  locale: string,
  interviewQuestionId: number,
  bucketFolder: string = 'resume_video',
  onComplete?: (success: boolean) => void,
): Promise<boolean> => {
  try {
    if (!file) {
      onComplete?.(false);
      return false;
    }

    const storageRef = ref(storage, `${STORAGE_BUCKETS_URI}/${bucketFolder}/${file.name}`);
    const uploadResult = await uploadBytes(storageRef, file);

    const payload = {
      id: file.name.match(/^jVideo_.*?_(.*?)\.(mp4|webm)$/)?.[1],
      name: uploadResult.metadata?.name,
      questionId: interviewQuestionId,
      locale,
    };

    const apiResponse = await fetcher<ApiResumeUploadVideo>(
      '/v1/job-interview/add-interview-video',
      locale,
      { method: 'POST', payload, stringifyPayload: true },
    );

    if (isResumeUploadVideoOk(apiResponse)) {
      onComplete?.(true);
      return true;
    }

    if (isApiV1Errors(apiResponse)) {
      bjToast.error(apiResponse.errors[0].message);
      onComplete?.(false);
    }

    return false;
  } catch (error) {
    console.error('Error uploading video:', error);
    bjToast.error('Failed to upload video');
    onComplete?.(false);
    return false;
  }
};
